import { ref, watchEffect, computed, reactive, Ref, watch } from "@vue/composition-api";
import { StaffAdministrationDto, StaffInfoService } from '@/generated/api.clients';
import Api from '@/plugin/Api';

const api = new StaffInfoService(undefined, Api);

export default function useStaffInfo(districtLea: Ref<string>): any {
  const staff = ref(new Array<StaffAdministrationDto>())
  const getStaffInfo = async () => {
    if (districtLea.value) {
      staff.value = await api.getAdministrativeStaff(districtLea.value)
    }
  }

  const superintendents = computed(() => {
    return staff.value
      .filter(f => f.jobFunction.toLowerCase().indexOf("superintendent") >= 0)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const otherAdministrators = computed(() => {
    return staff.value
      .filter(f => f.jobFunction.toLowerCase().indexOf("administrator / other") >= 0)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const effectivenessSystemCoordinators = computed(() => {
    return staff.value
      .filter(f => f.jobFunction.toLowerCase().indexOf("effectiveness system coordinator") >= 0)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const districtLevelGeneralObserver = computed(() => {
    return staff.value
      .filter(f => f.jobFunction.toLowerCase().indexOf("general observer") >= 0 && f.schoolLEA === f.districtLEA)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, schoolLEA: i.schoolLEA, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const districtLevelObservers = computed(() => {
    return [...superintendents.value, ...otherAdministrators.value, ...effectivenessSystemCoordinators.value, ...districtLevelGeneralObserver.value]
  })

  const generalObserverByLea = computed(() => (schoolLea) => {
    return staff.value.filter(f => f.jobFunction.toLowerCase().indexOf("general observer") >= 0 && f.schoolLEA === schoolLea)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, schoolLEA: i.schoolLEA, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const otherPotentialObservers = computed(() => {
    const otherJobFunctions = ['curriculum supervisor', 'special education supervisor', 'federal programs', 'journey worker']
    return staff.value.filter(f => otherJobFunctions.includes(f.jobFunction.toLowerCase()))
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, schoolLEA: i.schoolLEA, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const principals = computed(() => {
    return staff.value.filter(f => f.jobFunction.toLowerCase().indexOf("principal") >= 0)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, schoolLEA: i.schoolLEA, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const virtualEvaluators = computed(() => {
    return staff.value.filter(f => f.jobFunction.toLowerCase().indexOf("virtual evaluator") >= 0)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, schoolLEA: i.schoolLEA, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const principalsForDistrict = computed(() => {
    return staff.value.filter(f => f.jobFunction.toLowerCase().indexOf("principal") >= 0)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, schoolLEA: i.schoolLEA, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  const principalsByLea = computed(() => (schoolLea) => {
    return staff.value.filter(f => f.jobFunction.toLowerCase().indexOf("principal") >= 0 && f.schoolLEA === schoolLea)
      .map(i => { return { uniqueEmployeeId: i.uniqueEmployeeId, firstName: i.firstName, lastName: i.lastName, userId: i.userId, schoolLEA: i.schoolLEA, activeDirectoryUserId: i.activeDirectoryUserId } })
  })

  watchEffect(() => {
    if (districtLea.value) {
      getStaffInfo()
    }
  })

  return {
    staff: reactive({
      all: staff,
      superintendents,
      otherAdministrators,
      districtLevelGeneralObserver,
      districtLevelObservers,
      principals,
      principalsByLea,
      virtualEvaluators,
      effectivenessSystemCoordinators,
      generalObserverByLea,
      otherPotentialObservers,
      principalsForDistrict,
    })
  }
}

export function useCollaboratorSearch(): any {
  const collaboratorOptions:  Ref<{userId: string, label: string}[]> = ref([])
  const fetchCollaborators = async (searchString: string, loading: (loading: boolean) => void): Promise<void> => {
    if (searchString.length) {
      loading(true)
      collaboratorOptions.value = await (await api.getPotentialCollaborators(searchString)).map(p => {return { userId: p.userId, label: `${p.firstName} ${p.lastName} (${p.emailAddress})`}})
      loading(false)
    } else {
      collaboratorOptions.value = []
    }
  }

  return {
    fetchCollaborators,
    collaboratorOptions
  }
}